"use client"
import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { lazy, Suspense } from "react"
import {
  Calendar,
  Menu,
  X,
  ChevronDown,
  Play,
  ArrowRight,
  Check,
  Mic,
  Database,
  LogIn,
  CreditCard,
  Briefcase,
  UserPlus,
  Rocket,
  FilePlus,
  BarChart2,
  Scale,
  WifiOff,
  Layers,
  ShieldCheck,
} from "lucide-react"
import { motion } from "framer-motion"

import { MyteConstructionHelmet } from "../seo/helmetConfigs"
// Dynamically import ThreeSphere to avoid loading it on mobile or in SSR
const ThreeSphere = lazy(() => import("shared/components/ThreeSphere"))

/* -------------------------------------------------------------------------- */
/*                           2. UTILITY HOOKS                                 */
/* -------------------------------------------------------------------------- */

// Simple IntersectionObserver Hook to highlight nav
function useActiveSection(setActiveSection) {
  useEffect(() => {
    let timeoutId = null
    const handleScroll = () => {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        const scrollPosition = window.scrollY + 100
        const sections = document.querySelectorAll("section[id]") || []
        sections.forEach((section) => {
          const sectionTop = section.offsetTop
          const sectionHeight = section.offsetHeight
          const sectionId = section.getAttribute("id") || ""
          if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
            setActiveSection(sectionId)
          }
        })
      }, 100)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [setActiveSection])
}

/**
 * Very simple typed effect logic
 */
export function useFastTypewriter(typedElementRef, setShowTagline) {
  useEffect(() => {
    const phrases = [
      "Speak Your Job Site Needs into Existence",
      "Own Your Construction Technology",
      "Build Custom Solutions That Work for You",
      "Transform Data Entry with Voice Commands",
      "Automate Your Construction Workflow",
    ]
    let phraseIndex = 0
    let animationFrame
    const totalTypingTime = 500
    const totalDeletingTime = 500
    let isMounted = true

    const typePhrase = (phrase, cb) => {
      let start = null
      const update = (timestamp) => {
        if (!start) start = timestamp
        const elapsed = timestamp - start
        const progress = Math.min(elapsed / totalTypingTime, 1)
        const charCount = Math.floor(phrase.length * progress)
        if (typedElementRef.current) {
          typedElementRef.current.textContent = phrase.slice(0, charCount)
        }
        if (progress < 1) {
          animationFrame = requestAnimationFrame(update)
        } else {
          cb()
        }
      }
      animationFrame = requestAnimationFrame(update)
    }

    const deletePhrase = (phrase, cb) => {
      let start = null
      const update = (timestamp) => {
        if (!start) start = timestamp
        const elapsed = timestamp - start
        const progress = Math.min(elapsed / totalDeletingTime, 1)
        const charCount = phrase.length - Math.floor(phrase.length * progress)
        if (typedElementRef.current) {
          typedElementRef.current.textContent = phrase.slice(0, charCount)
        }
        if (progress < 1) {
          animationFrame = requestAnimationFrame(update)
        } else {
          cb()
        }
      }
      animationFrame = requestAnimationFrame(update)
    }

    const cyclePhrases = () => {
      if (!isMounted || !typedElementRef.current) return
      const currentPhrase = phrases[phraseIndex]
      typePhrase(currentPhrase, () => {
        setTimeout(() => {
          deletePhrase(currentPhrase, () => {
            phraseIndex = (phraseIndex + 1) % phrases.length
            cyclePhrases()
          })
        }, 1000)
      })
    }

    cyclePhrases()
    setShowTagline(true)

    return () => {
      isMounted = false
      cancelAnimationFrame(animationFrame)
    }
  }, [typedElementRef, setShowTagline])
}

/* -------------------------------------------------------------------------- */
/*                         3. SUBCOMPONENTS (HEADER, HERO, ETC.)             */
/* -------------------------------------------------------------------------- */

// 1) HEADER
function Header({ isScrolled, isMenuOpen, setIsMenuOpen, activeSection }) {
  const base = process.env.PUBLIC_URL || ""

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-[#0e1d34]/90 backdrop-blur-md shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <div className="flex justify-center">
          <Link to="/myteconstruction" className="flex items-center">
            <img
              src={`${base}/LogoFile.png`}
              alt="Myte Construction Logo"
              className="w-36 h-auto max-w-full object-contain"
              loading="lazy"
            />
          </Link>
        </div>
        {/* Main Nav */}
        <nav className="flex items-center gap-6">
          <div className="hidden md:flex gap-6">
            <a
              href="#products"
              className={`text-sm font-medium ${
                activeSection === "products" ? "text-white" : "text-blue-200"
              } hover:text-white transition-colors duration-300`}
            >
              Products
            </a>
            <a
              href="#features"
              className={`text-sm font-medium ${
                activeSection === "features" ? "text-white" : "text-blue-200"
              } hover:text-white transition-colors duration-300`}
            >
              Features
            </a>
            <a
              href="#pricing"
              className={`text-sm font-medium ${
                activeSection === "pricing" ? "text-white" : "text-blue-200"
              } hover:text-white transition-colors duration-300`}
            >
              Pricing
            </a>
            <Link
              to="/myteconstruction/video-demos"
              className={`text-sm font-medium ${
                activeSection === "video-demos" ? "text-white" : "text-blue-200"
              } hover:text-white transition-colors duration-300`}
            >
              Video Demos
            </Link>
          </div>
          {/* Buttons (Login / Schedule a Call) */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/myteconstruction/login"
              className="inline-flex items-center px-3 py-2 bg-transparent hover:bg-[#3080c1]/20 text-white rounded-lg transition-all duration-300"
            >
              <LogIn className="w-4 h-4 mr-2" /> Login/Register
            </Link>
            <a
              href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              <Calendar className="w-4 h-4 mr-2" /> Schedule a Call
            </a>
          </div>
          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white bg-transparent border-none"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>
      {/* Mobile Dropdown Nav */}
      {isMenuOpen && (
        <div className="md:hidden bg-[#0e1d34]/95 backdrop-blur-md p-4">
          <nav className="flex flex-col gap-4 max-w-7xl mx-auto">
            <a
              href="#products"
              className="text-lg text-blue-200 hover:text-white transition-colors duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              Products
            </a>
            <a
              href="#features"
              className="text-lg text-blue-200 hover:text-white transition-colors duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              Features
            </a>
            <a
              href="#pricing"
              className="text-lg text-blue-200 hover:text-white transition-colors duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              Pricing
            </a>
            <Link
              to="/myteconstruction/video-demos"
              className="text-lg text-blue-200 hover:text-white transition-colors duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              Video Demos
            </Link>
            <Link
              to="/myteconstruction/login"
              className="flex items-center px-4 py-3 bg-transparent hover:bg-[#3080c1]/20 text-white rounded-lg transition-all duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              <LogIn className="w-4 h-4 mr-2" /> Login/Register
            </Link>
            <a
              href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-4 py-3 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg transition-all duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              <Calendar className="w-4 h-4 mr-2" /> Schedule a Call
            </a>
          </nav>
        </div>
      )}
    </header>
  )
}

// 2) HERO
function HeroSection({ showTagline, typedElementRef }) {
  const textToAnimate = "⚙️ Laser-Focused Scaffold for the Steel Industry"
  const [zoomMultiplier, setZoomMultiplier] = useState(5.5)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768
      setIsMobile(mobile)
      setZoomMultiplier(mobile ? 3 : 5.5)
    }
    checkMobile()
    let resizeTimer
    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(checkMobile, 100)
    }
    window.addEventListener("resize", handleResize, { passive: true })
    return () => {
      if (resizeTimer) clearTimeout(resizeTimer)
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <section
      id="hero"
      className="pt-32 pb-4 px-4 relative overflow-visible lg:pt-40 lg:pb-8 bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a]"
    >
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left Side */}
          <div className="text-center lg:text-left">
            {showTagline && (
              <h2 className="text-xl md:text-2xl text-[#3080c1] font-semibold mb-4 lively-chars">
                {Array.from(textToAnimate).map((char, idx) => (
                  <span
                    key={idx}
                    className="inline-block"
                    style={{
                      animation: `livelyChar 0.4s ease-out forwards`,
                      animationDelay: `${0.03 * idx}s`,
                      opacity: 0,
                      willChange: "opacity, transform",
                    }}
                  >
                    {char === " " ? "\u00A0" : char}
                  </span>
                ))}
              </h2>
            )}

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-white transition-all duration-700 hover:scale-105">
              AI‑Centric Systems Built for Steel
            </h1>

            <p className="text-lg text-blue-200 mb-8 max-w-2xl mx-auto lg:mx-0">
              Purpose-built for structural steel estimators, project managers, and field ops.
              Generate takeoffs, BOMs, RFQs, and daily reporting with precision—powered by AI, driven by your voice.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start relative z-20">
              <a
                href="myteconstruction/login"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-[#3080c1]/30 flex items-center justify-center"
              >
                Start Free – Save Time Instantly
                <Rocket className="ml-2 w-4 h-4" />
              </a>

              <Link
                to="/myteconstruction/video-demos"
                className="px-6 py-3 bg-[#1e3a8a] hover:bg-[#1e40af] text-white rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-[#1e3a8a]/30 flex items-center justify-center"
              >
                <Play className="mr-2 w-4 h-4" /> See It in Action
              </Link>
            </div>

            <h1
              className="
                text-xl sm:text-2xl md:text-3xl lg:text-4xl
                font-bold mt-6
                leading-tight
                min-h-[4rem]            /* lock in one‐line height at largest size */
              "
              aria-label="AI construction technology benefits"
            >
              <span
                id="typed-headlines"
                ref={typedElementRef}
                className="
                  inline-block
                  whitespace-nowrap         /* never wrap */
                  overflow-hidden           /* hide the cursor beyond the edge */
                "
              />
            </h1>
          </div>

          {/* Right Side */}
          <div className="hidden lg:block relative">
            <div className="sphere-container absolute top-0 right-[-5%] w-full h-full flex justify-center items-center">
              {!isMobile && (
                <Suspense
                  fallback={
                    <div className="sphere-placeholder h-[400px] md:h-[500px] lg:h-[600px] bg-[#1e3a8a]/20 rounded-xl" />
                  }
                >
                  <ThreeSphere zoomMultiplier={zoomMultiplier} />
                </Suspense>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 cursor-pointer group">
        <a href="#products" className="block">
          <ChevronDown className="w-8 h-8 text-blue-400 animate-bounce group-hover:text-white transition-colors duration-300" />
          <span className="text-xs text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 absolute left-1/2 transform -translate-x-1/2 mt-1">
            Scroll Down
          </span>
        </a>
      </div>
    </section>
  )
}


/* -------------- About Section -------------- */
function AboutSection({ isVisible }) {
  const base = process.env.PUBLIC_URL || ""

  return (
    <section
      id="about"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.about ? "opacity-100 bg-gradient-to-br from-[#3080c1]/10 to-[#0e1d34]/90" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          {/* 📸 IMAGE - now on the left */}
          <div className="flex justify-center order-1 md:order-1">
            <div className="relative w-48 h-48 md:w-64 md:h-64">
              <div className="absolute inset-0 bg-[#3080c1]/20 rounded-full blur-xl animate-pulse" />
              <div className="w-full h-full rounded-full border-4 border-[#3080c1] shadow-xl relative z-10 overflow-hidden">
                <img
                  src={`${base}/AhmedStageImpact.jpg`}
                  alt="Ahmed Mekallach in the Field with Hardhat"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* 🧠 TEXT - now on the right */}
          <div className="order-2 md:order-2">
            <span className="inline-block px-4 py-1 mb-4 text-sm font-semibold tracking-wide uppercase bg-[#3080c1]/30 text-blue-100 rounded-full">
              Built by Someone Who Gets It
            </span>

            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-100">
              I’ve Been in Your Boots—Now I’m Building Your Systems
            </h2>

            <p className="text-base md:text-lg text-blue-200 mb-4">
              I’m Ahmed Mekallach. I’ve managed chaos in steel estimating and construction firsthand—job sites, payroll
              rooms, all of it. That’s why I built Myte Construction.
            </p>

            <p className="text-base md:text-lg text-blue-200 mb-4">
              This isn’t off-the-shelf software. It’s an adaptive intelligence layer built for the steel industry—voice-powered,
              fully ownable, and deeply aligned with how your foremen, estimators, and PMs actually work.
            </p>

            <div className="flex flex-wrap gap-4 mt-4">
              <Link
                to="/about"
                className="text-sm font-medium text-blue-300 hover:text-white transition"
              >
                Learn more about the mission →
              </Link>
              <Link
                to="/myteconstruction/video-demos"
                className="text-sm font-medium text-blue-300 hover:text-white transition"
              >
                Watch real builder reactions →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* -------------- Features Section -------------- */
function FeaturesSection({ isVisible }) {
  const featuresArray = [
    {
      title: "AI Take-off + BOM",
      desc: "Upload structural drawings, auto-detect steel members and generate a Bill-of-Materials in minutes.",
      icon: <FilePlus className="w-10 h-10 mb-4 text-blue-400" />,
    },
    {
      title: "Estimator Workload Dashboard",
      desc: "See who's working on what (and what's next) at a glance. No more spreadsheet chase-downs.",
      icon: <BarChart2 className="w-10 h-10 mb-4 text-blue-400" />,
    },
    {
      title: "Supplier Quote Comparison",
      desc: "Fire off RFQs and stack incoming quotes side-by-side. Find the best deal instantly.",
      icon: <Scale className="w-10 h-10 mb-4 text-blue-400" />,
    },
    {
      title: "Offline First, Online Sync",
      desc: "Capture quantities on site with zero signal; everything syncs the moment you're back online.",
      icon: <WifiOff className="w-10 h-10 mb-4 text-blue-400" />,
    },
    {
      title: "Full Cost-Code Control",
      desc: "Assign, track and report costs by code so every bid stays on budget (and on record).",
      icon: <Layers className="w-10 h-10 mb-4 text-blue-400" />,
    },
    {
      title: "Your Data, Your Rules",
      desc: "Self-host or let us host; extend the source or keep it vanilla. No black-box AI, ever.",
      icon: <ShieldCheck className="w-10 h-10 mb-4 text-blue-400" />,
    },
  ]

  return (
    <section
      id="features"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.features ? "opacity-100 bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a]/90" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Badge + headline */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 mb-4 text-sm font-semibold tracking-wide uppercase bg-[#3080c1]/30 text-blue-100 rounded-full">
            Estimating Suite
          </span>

          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">
            From first take-off to final quote - all in one place
          </h2>

          <p className="text-base md:text-lg text-blue-200 max-w-3xl mx-auto">
            Six tightly-integrated tools that turn hours of manual estimating into minutes of AI-assisted precision —
            perfect for steel fabricators and GC teams alike.
          </p>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuresArray.map((feature, idx) => (
            <div
              key={idx}
              className="bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] p-6 rounded-xl border border-[#3080c1]/30 transition-all duration-500 hover:border-[#3080c1] hover:shadow-lg hover:shadow-[#3080c1]/20 hover:-translate-y-1"
            >
              {feature.icon}
              <h3 className="text-xl font-bold mb-2 text-[#3080c1] hover:text-blue-300 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="text-blue-200">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* -------------- Benefits Section -------------- */
function BenefitsSection({ isVisible }) {
  const benefitsData = [
    {
      title: "Win Bids Faster",
      desc: "Automate the heavy lifting of takeoffs and costing so your estimators can focus on pricing strategy.",
      icon: <Rocket className="w-12 h-12 mb-4 text-blue-400" />,
    },
    {
      title: "Unified Project Visibility",
      desc: "All bids, RFQs and cost codes live in one dashboard—never hunt through spreadsheets again.",
      icon: <Database className="w-12 h-12 mb-4 text-blue-400" />,
    },
    {
      title: "Multiply Estimator Productivity",
      desc: "From PDF takeoff to supplier outreach, your team operates like a force multiplier—5× faster turnarounds.",
      icon: <UserPlus className="w-12 h-12 mb-4 text-blue-400" />,
    },
  ]

  return (
    <section
      id="benefits"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.benefits ? "opacity-100 bg-gradient-to-br from-[#1e3a8a]/80 to-[#0e1d34]/90" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Badge + headline */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 mb-4 text-sm font-semibold tracking-wide uppercase bg-[#3080c1]/30 text-blue-100 rounded-full">
            Instant ROI
          </span>

          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">
            Immediate Benefits for Your Team
          </h2>

          <p className="text-base md:text-lg text-blue-200 max-w-3xl mx-auto">
            From day one your crew will save hours every week with AI-driven estimating, unified data, and zero manual
            handoffs.
          </p>
        </div>

        {/* Benefit cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {benefitsData.map((b, idx) => (
            <div
              key={idx}
              className="text-center p-6 rounded-xl bg-[#1e3a8a]/20 border border-[#3080c1]/30 transition-all duration-500 hover:border-[#3080c1] hover:shadow-lg hover:shadow-[#3080c1]/20 hover:-translate-y-1"
            >
              {b.icon}
              <h3 className="text-xl font-bold mb-3 text-[#3080c1]">{b.title}</h3>
              <p className="text-blue-200">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function SuiteSection({ isVisible }) {
  const suiteItems = [
    {
      title: "Timesheets",
      desc: "Voice‑driven, cost‑coded time reporting.",
      icon: <Mic className="w-8 h-8 text-blue-400 mb-3" />,
      link: "/myteconstruction/timesheets",
    },
    {
      title: "RFIs",
      desc: "Request and track field inquiries seamlessly.",
      icon: <Briefcase className="w-8 h-8 text-blue-400 mb-3" />,
      link: "/rfis",
    },
    {
      title: "Daily Field Reports",
      desc: "Capture and review daily site activities in real time.",
      icon: <FilePlus className="w-8 h-8 text-blue-400 mb-3" />,
      link: "/daily-reports",
    },
    {
      title: "Risk Assessments",
      desc: "Conduct on‑site risk checks with guided templates.",
      icon: <ShieldCheck className="w-8 h-8 text-blue-400 mb-3" />,
      link: "/risk-assessments",
    },
    {
      title: "Picklists",
      desc: "Generate and manage material picklists instantly.",
      icon: <Layers className="w-8 h-8 text-blue-400 mb-3" />,
      link: "/picklists",
    },
    {
      title: "KPI Tracking",
      desc: "Visualize key project metrics on one dashboard.",
      icon: <BarChart2 className="w-8 h-8 text-blue-400 mb-3" />,
      link: "/kpi",
    },
    {
      title: "Cost‑Coded Timesheets",
      desc: "Assign cost codes on‑the‑fly via voice commands.",
      icon: <CreditCard className="w-8 h-8 text-blue-400 mb-3" />,
      link: "/cost-coding",
    },
    {
      title: "PIDO Management",
      desc: "Create and process purchase orders and invoices.",
      icon: <Rocket className="w-8 h-8 text-blue-400 mb-3" />,
      link: "/pido",
    },
    {
      title: "Rental Tracking",
      desc: "Monitor rented gear, due dates, and returns.",
      icon: <Database className="w-8 h-8 text-blue-400 mb-3" />,
      link: "/rental-tracking",
    },
    {
      title: "Change Order Control",
      desc: "Issue and audit change orders end‑to‑end.",
      icon: <Scale className="w-8 h-8 text-blue-400 mb-3" />,
      link: "/change-orders",
    },
  ]

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariant = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  }

  return (
    <section
      id="suite"
      className={`relative py-24 px-4 transition-opacity duration-500 ${
        isVisible.suite ? "opacity-100 bg-gradient-to-b from-[#040d21] via-[#0e1d34] to-[#1e3a8a]" : "opacity-0"
      }`}
    >
      {/* Decorative vertical gradient line */}
      <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-full w-[2px] bg-gradient-to-b from-transparent via-[#3080c1]/40 to-transparent"></div>

      <div className="max-w-7xl mx-auto">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible.suite ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-3xl mx-auto"
        >
          <span className="inline-block px-4 py-1 mb-4 text-xs md:text-sm font-semibold tracking-widest uppercase bg-[#3080c1]/30 text-blue-100 rounded-full">
            AI‑Infused Workflow
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-blue-100 mb-4 drop-shadow-xl">
            Waterfall of Possibilities
          </h2>
          <p className="text-base md:text-lg text-blue-200">
            From first voice command to final payment, every workflow cascades seamlessly through one intelligent
            platform.
          </p>
        </motion.div>

        {/* Waterfall grid */}
        <motion.div
          variants={container}
          initial="hidden"
          animate={isVisible.suite ? "show" : "hidden"}
          className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10"
        >
          {suiteItems.map((tool, idx) => (
            <motion.div
              key={tool.title}
              variants={itemVariant}
              className={`relative flex flex-col p-6 rounded-3xl bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] border border-[#3080c1]/30 backdrop-blur-md shadow-[0_10px_25px_-5px_rgba(48,128,193,0.4)] transition-transform duration-300 hover:-translate-y-2 hover:shadow-[0_15px_30px_-5px_rgba(48,128,193,0.6)]`}
              style={{ marginTop: `${(idx % 4) * 1.5}rem` }}
            >
              {tool.icon}
              <h3 className="text-lg md:text-xl font-bold mb-2 text-[#60a5fa]">{tool.title}</h3>
              <p className="text-blue-200 mb-4 flex-grow leading-relaxed">{tool.desc}</p>
              <Link
                to={tool.link}
                className="inline-flex justify-center items-center w-full py-2 mt-auto rounded-lg bg-[#3080c1] hover:bg-[#2563eb] text-white font-medium tracking-wide transition-colors duration-300"
              >
                Explore
              </Link>
              {/* glow accent */}
              <span className="pointer-events-none absolute -inset-px rounded-3xl bg-gradient-to-br from-transparent via-[#3080c1]/30 to-transparent blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

/* -------------- Pricing Section -------------- */
function PricingSection({ isVisible, activeTab, setActiveTab }) {
  return (
    <section
      id="pricing"
      className={`py-20 px-4 transition-opacity duration-300 ${
        isVisible.pricing
          ? "opacity-100 bg-gradient-to-br from-[#0e1d34]/90 to-[#1e3a8a]/90"
          : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* ----- header ----- */}
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">
            Own the Tech, Pay for the&nbsp;Results
          </h2>
          <p className="text-base md:text-lg text-blue-200 max-w-3xl mx-auto">
            Every plan erases admin hours and wins bids faster—choose the level of
            ownership that fits your crew.
          </p>
        </div>

        {/* ----- tabs ----- */}
        <div className="flex justify-center mb-10">
          <div
            className={`inline-flex bg-[#1e3a8a]/30 rounded-lg p-1 ${
              isVisible.pricing ? "opacity-100" : "opacity-0"
            } transition-opacity duration-300`}
          >
            {[
              ["free", "Explore"],
              ["paid", "Scale"],
              ["custom", "Done‑For‑You"]
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => {
                  setActiveTab(key)
                  window.gtag?.("event", "pricing_tab_click", { tab: key })
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeTab === key
                    ? "bg-[#3080c1] text-white shadow-md"
                    : "text-blue-200 hover:text-white hover:bg-[#3080c1]/20"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ----- cards ----- */}
        <div className="max-w-3xl mx-auto">
          {/* === EXPLORE (free) === */}
          {activeTab === "free" && (
            <PlanCard
              title="Sandbox (30 days)"
              price="$0"
              subtitle="Spin up a steel‑smart workspace in minutes"
              bullets={[
                "1 active project & 1 foreman seat",
                "Steel‑specific take‑off + timesheet templates",
                "Voice input & basic validation",
                "Export to Excel / PDF",
                "Email & community support"
              ]}
              ctaLabel="Launch My Sandbox"
              ctaHref="/myteconstruction/login"
              isVisible={isVisible.pricing}
            />
          )}

          {/* === SCALE (subscription) === */}
          {activeTab === "paid" && (
            <PlanCard
              badge="Most Teams Start Here"
              title="Construction Suite"
              price="$200"
              priceSuffix="/seat / month"
              subtitle="Unlimited projects. Unlimited viewers."
              bullets={[
                "Save 5+ admin hours per foreman every week",
                "AI take‑offs, voice‑coded timesheets, RFIs & BOMs",
                "Live cost‑code dashboards",
                "Unlimited PM / viewer accounts",
                "Dedicated onboarding specialist"
              ]}
              ctaLabel="Start Roll‑out"
              ctaHref="/myteconstruction/login"
              secondaryCta={{
                label: "Schedule Demo",
                href: "https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
              }}
              isVisible={isVisible.pricing}
            />
          )}

          {/* === DONE‑FOR‑YOU (custom) === */}
          {activeTab === "custom" && (
            <PlanCard
              badge="Total Care"
              title="Full‑Stack Partner"
              price="$8 500"
              priceSuffix="/month flat"
              subtitle="We design, build, host & iterate—your digital steel team."
              bullets={[
                "Unlimited seats, projects & storage",
                "Custom feature design & rapid dev sprints",
                "24 × 7 monitoring & SLA",
                "Quarterly AI‑model tune‑ups",
                "On‑site workshops & change‑management",
                "Single‑tenant cloud or on‑prem option"
              ]}
              ctaLabel="Book 30‑min Blueprint Call"
              ctaHref="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
              isVisible={isVisible.pricing}
            />
          )}
        </div>
      </div>
    </section>
  )
}

/* ---------- helper component for brevity ---------- */
function PlanCard({
  badge,
  title,
  price,
  priceSuffix,
  subtitle,
  bullets,
  ctaLabel,
  ctaHref,
  secondaryCta,
  isVisible
}) {
  return (
    <div
      className={`relative bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] p-8 rounded-xl border border-[#3080c1]/50 shadow-lg ${
        isVisible ? "opacity-100" : "opacity-0"
      } transition-opacity duration-300`}
    >
      {badge && (
        <div className="absolute top-0 right-0 bg-[#3080c1] text-white px-4 py-1 text-xs md:text-sm font-semibold rounded-bl-lg">
          {badge}
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-white mb-2">{title}</h3>
        <div className="text-4xl font-bold text-white mb-2">
          {price}
          {priceSuffix && (
            <span className="text-xl font-normal"> {priceSuffix}</span>
          )}
        </div>
        <p className="text-lg md:text-xl text-blue-200">{subtitle}</p>
      </div>

      <ul className="space-y-3 mb-8">
        {bullets.map((b) => (
          <li key={b} className="flex items-start">
            <Check
              className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0"
              aria-hidden="true"
            />
            <span className="text-blue-100">{b}</span>
          </li>
        ))}
      </ul>

      <div className="text-center flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to={ctaHref}
          className="inline-flex items-center justify-center px-8 py-3 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
        >
          {ctaLabel} <ArrowRight className="ml-2 w-5 h-5" />
        </Link>

        {secondaryCta && (
          <a
            href={secondaryCta.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-3 bg-[#1e3a8a] hover:bg-[#1e40af] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
          >
            {secondaryCta.label} <Calendar className="ml-2 w-5 h-5" />
          </a>
        )}
      </div>
    </div>
  )
}

/* -------------- AI Demo CTA Section -------------- */
function AIDemoCTASection({ isVisible }) {
  return (
    <section
      id="demo"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.demo ? "opacity-100 bg-gradient-to-br from-[#1e3a8a] to-[#3080c1]/30" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">
          Ready to Transform Your Workflow?
        </h2>
        <p className="text-base md:text-lg text-blue-200 mb-8 max-w-3xl mx-auto">
          Book a live demo and see how Myte Construction saves your crew hours every week.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/joinMyte"
            className="px-8 py-4 bg-[#1e3a8a] hover:bg-[#1e40af] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
          >
            <Play className="mr-2 w-5 h-5" /> Watch Demos
          </Link>
          <a
            href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
          >
            <Calendar className="mr-2 w-5 h-5" /> Book Your Demo Now
          </a>
        </div>
      </div>
    </section>
  )
}

/* -------------- Vision Section -------------- */
function VisionSection({ isVisible }) {
  return (
    <section
      id="vision"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.vision ? "opacity-100 bg-gradient-to-b from-[#1e3a8a] to-[#0e1d34]" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">
          Own Your Tech, Own Your Future
        </h2>
        <p className="text-base md:text-lg text-blue-200 mb-8 max-w-3xl mx-auto">
          Myte Construction isn't just software—it's your path to digital autonomy. Take control of your data and
          workflows in the AI era.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/joinMyte"
            className="inline-flex items-center px-8 py-4 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
          >
            Start Your Free Trial Today <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
          <a
            href="https://www.mytecody.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-[#1e3a8a] hover:bg-[#1e40af] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
          >
            Start Your Roadmap <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  )
}

/* -------------- Footer -------------- */
function Footer() {
  const base = process.env.PUBLIC_URL || ""

  return (
    <footer className="py-12 px-4 bg-[#0e1d34] border-t border-[#3080c1]/50">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="font-bold text-lg text-blue-100">Myte Construction</span>
            </div>
            <p className="text-sm text-blue-200 mb-4 max-w-md">
              Powering the steel industry’s next leap—custom-built, AI-augmented systems designed by builders, for builders.
            </p>
            <p className="text-xs text-blue-300">
              © {new Date().getFullYear()} Myte Construction. All rights reserved.
            </p>
          </div>
          <div className="md:col-span-3">
            <h3 className="text-lg font-bold mb-4 text-blue-100">Quick Links</h3>
            <ul className="flex flex-col gap-2">
              <li>
                <a href="#features" className="text-blue-200 hover:text-white transition-colors duration-300">
                  Features
                </a>
              </li>
              <li>
                <a href="#pricing" className="text-blue-200 hover:text-white transition-colors duration-300">
                  Pricing
                </a>
              </li>
              <li>
                <a href="#community" className="text-blue-200 hover:text-white transition-colors duration-300">
                  Trusted by Innovators
                </a>
              </li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <h3 className="text-lg font-bold mb-4 text-blue-100">Contact</h3>
            <ul className="flex flex-col gap-2">
              <li>
                <a
                  href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-200 hover:text-white transition-colors duration-300"
                >
                  Schedule a Call
                </a>
              </li>
              <li>
                <Link
                  to="/myteconstruction/video-demos"
                  className="text-blue-200 hover:text-white transition-colors duration-300"
                >
                  Video Demos
                </Link>
              </li>
              <li>
                <Link
                  to="/myteconstruction/login"
                  className="text-blue-200 hover:text-white transition-colors duration-300"
                >
                  Login
                </Link>
              </li>
            </ul>
          </div>
          <div className="md:col-span-2 flex justify-center md:justify-end items-start">
            <Link to="/myteconstruction" className="flex items-center">
              <img src={`${base}/LogoFile.png`} alt="Myte Construction Logo" className="w-24 h-auto" loading="lazy" />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

/* -------------------------------------------------------------------------- */
/*                           4. MAIN LANDING PAGE                             */
/* -------------------------------------------------------------------------- */
export default function ALandingPage() {
  // Remove the unused base variable from the main component
  // const base = process.env.PUBLIC_URL || "" - This was causing the no-unused-vars error

  // Mobile menu
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  // Nav highlight
  const [activeSection, setActiveSection] = useState("hero")
  // Header scroll effect
  const [isScrolled, setIsScrolled] = useState(false)
  // Visibility states
  const [isVisible, setIsVisible] = useState({ hero: true })
  // Pricing toggle
  const [activeTab, setActiveTab] = useState("paid")

  // Tagline
  const [showTagline, setShowTagline] = useState(false)

  const typedElementRef = useRef(null)

  // Initialize typed headlines
  useFastTypewriter(typedElementRef, setShowTagline)

  // Scroll performance
  useEffect(() => {
    let lastScrollY = window.scrollY
    let ticking = false
    const handleScroll = () => {
      lastScrollY = window.scrollY
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(lastScrollY > 10)
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Use the custom hook
  useActiveSection(setActiveSection)

  // IntersectionObserver for each section
  useEffect(() => {
    const sectionIds = ["products", "about", "features", "benefits", "suite", "pricing", "demo", "community", "vision"]
    const observerOptions = { root: null, rootMargin: "0px", threshold: 0.1 }
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id
          setIsVisible((prev) => ({ ...prev, [sectionId]: true }))
          sectionObserver.unobserve(entry.target)
        }
      })
    }, observerOptions)

    sectionIds.forEach((id) => {
      const el = document.getElementById(id)
      if (el) sectionObserver.observe(el)
    })

    return () => {
      sectionObserver.disconnect()
    }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a] text-white relative">
      <MyteConstructionHelmet />

      {/* Header */}
      <Header
        isScrolled={isScrolled}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        activeSection={activeSection}
      />

      {/* Hero */}
      <HeroSection showTagline={showTagline} typedElementRef={typedElementRef} />

      {/* About */}
      <AboutSection isVisible={isVisible} />

      {/* Features */}
      <FeaturesSection isVisible={isVisible} />

      {/* Benefits */}
      <BenefitsSection isVisible={isVisible} />

      {/* AI Integration */}
      <SuiteSection isVisible={isVisible} />

      {/* Pricing */}
      <PricingSection isVisible={isVisible} activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* AI Demo CTA */}
      <AIDemoCTASection isVisible={isVisible} />

      {/* Vision */}
      <VisionSection isVisible={isVisible} />

      {/* Footer */}
      <Footer />
    </div>
  )
}
